import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router";
import { ArrowLeft, File02, ClockRewind } from "@untitledui/icons";
import { documentService } from "@/data/mock-documents";
import { documentVersionService } from "@/data/mock-document-versions";
import { containerService } from "@/data/mock-containers";
import { ActivityFeed } from "@/components/application/activity-feed/activity-feed";
import type { DocumentVersion } from "@/types/container";
import type { ActivityItem } from "@/types/activity";
import { useTranslation } from "react-i18next";
import { useI18n } from "@/hooks/use-i18n";

export default function ShipperDocumentHistoryPage() {
    const { t } = useTranslation(['pages:documents', 'common']);
    const { translateDocumentType } = useI18n();
    const { containerId, documentId } = useParams<{ containerId: string; documentId: string }>();
    const [versions, setVersions] = useState<DocumentVersion[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const doc = useMemo(() => (documentId ? documentService.getById(documentId) : undefined), [documentId]);
    const container = useMemo(() => (containerId ? containerService.getById(containerId) : undefined), [containerId]);

    useEffect(() => {
        if (!documentId) return;

        setIsLoading(true);
        const documentVersions = documentVersionService.getByDocumentId(documentId);
        setVersions(documentVersions);
        setIsLoading(false);
    }, [documentId]);

    // Convert DocumentVersion to ActivityItem for display
    const activityItems: ActivityItem[] = useMemo(() => {
        return versions.map((version) => ({
            id: version.id,
            audience: "shipper" as const,
            type: "document_updated" as const,
            title: `${t('history.info.version')} ${version.version} - ${version.fileName}`,
            description: `${t('history.description.uploadedBy')} ${version.uploadedBy.name}`,
            timestamp: version.uploadedAt,
            isRead: true,
            actor: {
                name: version.uploadedBy.name,
                avatarUrl: version.uploadedBy.avatarUrl,
            },
            attachments: version.fileSizeKB
                ? [
                    {
                        name: version.fileName,
                        type: "pdf" as const,
                        sizeKB: version.fileSizeKB,
                    },
                ]
                : undefined,
            metadata: {
                documentId: version.documentId,
                documentType: doc?.type,
            },
        }));
    }, [versions, doc, t]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <p className="text-lg text-tertiary">{t('history.loading')}</p>
            </div>
        );
    }

    if (!doc) {
        return (
            <div className="text-center py-12">
                <File02 className="size-12 mx-auto mb-4 text-tertiary" />
                <h3 className="text-lg font-medium text-primary mb-2">{t('history.notFound.title')}</h3>
                <p className="text-tertiary mb-4">{t('history.notFound.message')}</p>
                <Link
                    to={`/shipper/containers/${containerId}`}
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand-solid px-4 py-2.5 text-md font-semibold dark:text-white shadow-xs-skeumorphic ring-1 ring-transparent ring-inset hover:bg-brand-solid_hover"
                >
                    <ArrowLeft className="size-5" />
                    {t('history.notFound.backToContainer')}
                </Link>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Link
                        to={`/shipper/containers/${containerId}?tab=documents`}
                        className="text-tertiary hover:text-primary"
                    >
                        <ArrowLeft className="size-5" />
                    </Link>
                    <div>
                        <h1 className="text-2xl font-semibold text-primary">{t('history.title')}</h1>
                        <p className="text-tertiary mt-1">
                            {doc.name}
                            {container && (
                                <span className="ml-2">
                                    - {t('history.container')} {container.containerNumber}
                                </span>
                            )}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2 text-tertiary">
                    <ClockRewind className="size-5" />
                    <span className="text-sm">{versions.length} {versions.length !== 1 ? t('history.versions.versions') : t('history.versions.count')}</span>
                </div>
            </div>

            {/* Document Info Card */}
            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <div className="text-sm font-medium text-primary mb-1">{t('history.info.documentType')}</div>
                        <div className="text-sm text-tertiary">{translateDocumentType(doc.type)}</div>
                    </div>
                    <div>
                        <div className="text-sm font-medium text-primary mb-1">{t('history.info.currentVersion')}</div>
                        <div className="text-sm text-tertiary">
                            {t('history.info.version')} {versions.length > 0 ? versions[0].version : 1}
                        </div>
                    </div>
                </div>
            </div>

            {/* Activity Feed */}
            {versions.length === 0 ? (
                <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary text-center">
                    <ClockRewind className="size-12 mx-auto mb-4 text-tertiary" />
                    <h3 className="text-lg font-medium text-primary mb-2">{t('history.empty.title')}</h3>
                    <p className="text-tertiary">{t('history.empty.message')}</p>
                </div>
            ) : (
                <ActivityFeed
                    items={activityItems}
                    renderItem={(item) => (
                        <div className="flex items-start gap-3">
                            <div className="mt-1">
                                <ClockRewind className="size-5 text-brand-secondary" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <h4 className="text-sm font-medium text-primary">{item.title}</h4>
                                </div>
                                {item.description && (
                                    <p className="mt-1 text-sm text-tertiary">{item.description}</p>
                                )}
                                {item.attachments && item.attachments.length > 0 && (
                                    <div className="mt-2 flex flex-wrap gap-2">
                                        {item.attachments.map((att, idx) => (
                                            <span
                                                key={idx}
                                                className="inline-flex items-center gap-1 rounded-md bg-secondary px-2 py-1 text-xs text-tertiary ring-1 ring-secondary"
                                            >
                                                <File02 className="size-3" /> {att.name}
                                                {att.sizeKB && ` (${att.sizeKB} KB)`}
                                            </span>
                                        ))}
                                    </div>
                                )}
                                <div className="mt-2 flex items-center gap-3 text-xs text-tertiary">
                                    <span>
                                        {new Intl.DateTimeFormat("it-IT", {
                                            day: "2-digit",
                                            month: "short",
                                            year: "numeric",
                                            hour: "2-digit",
                                            minute: "2-digit",
                                        }).format(item.timestamp)}
                                    </span>
                                    {item.actor && (
                                        <span className="flex items-center gap-1">
                                            <span>•</span>
                                            {item.actor.name}
                                            {item.actor.avatarUrl && (
                                                <img
                                                    src={item.actor.avatarUrl}
                                                    alt={item.actor.name}
                                                    className="size-4 rounded-full"
                                                />
                                            )}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                />
            )}
        </div>
    );
}

